/**
 * ThemeConstants.ts
 * Static mapping of semantic theme classes to the 7-step coordinate system.
 * These classes resolve via CSS variables which handle the light/dark flip.
 */

export const ThemeBackground = {
  // --- MAIN SURFACES ---
  mainBackground: 'bg-user-gray-1',
  mainBackgroundHover: 'hover:bg-user-gray-2',
  mainBackgroundInverted: 'bg-user-gray-7',
  mainBackgroundInvertedHover: 'hover:bg-user-gray-6',

  // --- ELEVATED SURFACES ---
  secondaryBackground: 'bg-user-gray-2',
  secondaryBackgroundHover: 'hover:bg-user-gray-3',
  secondaryBackgroundInverted: 'bg-user-gray-6',
  secondaryBackgroundInvertedHover: 'hover:bg-user-gray-5',

  tertiaryBackground: 'bg-user-gray-3',
  tertiaryBackgroundHover: 'hover:bg-user-gray-4',
  tertiaryBackgroundInverted: 'bg-user-gray-5',
  tertiaryBackgroundInvertedHover: 'hover:bg-user-gray-4',

  // --- BORDERS & DIVIDERS ---
  borderBackground: 'border-user-gray-3',
  borderBackgroundHover: 'hover:border-user-gray-4',
  borderBackgroundInverted: 'border-user-gray-6',
  borderBackgroundInvertedHover: 'hover:border-user-gray-5',
  divideBackground: 'divide-user-gray-3',
  divideHoverBackground: 'divide-user-gray-4',
  divideBackgroundSecondary: 'divide-user-gray-4',

  // --- SPECIAL STATES ---
  intenseBackground: 'bg-user-gray-1',
  backdropBackground: 'bg-user-gray-4/50',
  mainRing: 'ring-user-gray-3',
  mainRingHover: 'hover:ring-user-gray-4',

  // --- SECONDARY/INTENSE BORDERS ---
  borderBackgroundSecondary: 'border-user-gray-4',
  borderBackgroundSecondaryInverted: 'border-user-gray-5',
  borderBackgroundIntense: 'border-user-gray-7',
  borderBackgroundMuted: 'border-user-gray-1',
  secondaryRing: 'ring-user-gray-2',
  secondaryRingHover: 'hover:ring-user-gray-3',

  shadow: 'shadow shadow-user-gray-7/20',
  shadowInverted: 'shadow shadow-user-gray-1/20',
} as const;

export const ThemeText = {
  mainText: 'text-user-gray-7',
  mainTextHover: 'hover:text-user-gray-6',
  mainTextInverted: 'text-user-gray-1',
  mainTextInvertedHover: 'hover:text-user-gray-2',
  secondaryText: 'text-user-gray-6',
  secondaryTextHover: 'hover:text-user-gray-5',
  secondaryTextInverted: 'text-user-gray-2',
  tertiaryText: 'text-user-gray-5',
  tertiaryTextHover: 'hover:text-user-gray-4',
  tertiaryTextInverted: 'text-user-gray-3',
  placeholderText: 'placeholder:text-user-gray-4',
  placeholderTextMuted: 'placeholder:text-user-gray-3',
  placeholderTextIntense: 'placeholder:text-user-gray-5',
} as const;

export const ThemePrimary = {
  primaryBackground: 'bg-user-primary-4',
  primaryBackgroundHover: 'hover:bg-user-primary-5',
  primaryBackgroundGroupHover: 'group-hover:bg-user-primary-5',
  primaryBackgroundMuted: 'bg-user-primary-3',
  primaryBackgroundMutedHover: 'hover:bg-user-primary-4',
  primaryBackgroundUltraMuted: 'bg-user-primary-1',
  primaryBackgroundUltraMutedHover: 'hover:bg-user-primary-2',
  primaryBackgroundIntense: 'bg-user-primary-6',
  primaryBackgroundIntenseHover: 'hover:bg-user-primary-7',
  primaryBackgroundUltraIntense: 'bg-user-primary-7',
  primaryBackgroundUltraIntenseHover: 'hover:bg-user-primary-6',
  primaryShadow: 'shadow shadow-user-primary-4/50',
  primaryFocusOutline: 'focus-visible:outline-user-primary-4',
  primaryFocusRing: 'focus:ring-user-primary-5',
  primaryRing: 'ring-user-primary-5',
  primaryRingHover: 'hover:ring-user-primary-4',
  primaryRingMuted: 'ring-user-primary-5',
  primaryRingMutedHover: 'hover:ring-user-primary-4',
  primaryText: 'text-user-primary-4',
  primaryTextHover: 'hover:text-user-primary-5',
  primaryTextIntense: 'text-user-primary-6',
  primaryTextIntenseHover: 'hover:text-user-primary-7',
  primaryTextUltraIntense: 'text-user-primary-7',
  primaryTextMuted: 'text-user-primary-3',
  primaryTextUltraMuted: 'text-user-primary-2',
  primaryTextMutedHover: 'hover:text-user-primary-4',
  primaryGroupTextHover: 'group-hover:text-user-primary-5',
  primaryBorder: 'border-user-primary-4',
  primaryBorderHover: 'hover:border-user-primary-5',
  primaryBorderGroupHover: 'group-hover:border-user-primary-5',
  primaryBorderIntense: 'border-user-primary-6',
  primaryBorderIntenseHover: 'hover:border-user-primary-7',
  primaryBorderUltraIntense: 'border-user-primary-7',
  primaryBorderMuted: 'border-user-primary-3',
  primaryBorderMutedHover: 'hover:border-user-primary-4',
  primaryBorderUltraMuted: 'border-user-primary-1',
} as const;

export const ThemeSecondary = {
  secondaryText: 'text-user-secondary-4',
  secondaryTextHover: 'hover:text-user-secondary-5',
  secondaryTextIntense: 'text-user-secondary-6',
  secondaryTextUltraIntense: 'text-user-secondary-7',
  secondaryTextIntenseHover: 'hover:text-user-secondary-5',
  secondaryTextMuted: 'text-user-secondary-3',
  secondaryTextUltraMuted: 'text-user-secondary-2',
  secondaryTextMutedHover: 'hover:text-user-secondary-4',
  secondaryGroupTextHover: 'group-hover:text-user-secondary-5',
  secondaryShadow: 'shadow shadow-user-secondary-4/50',
  secondaryBackground: 'bg-user-secondary-4',
  secondaryBackgroundHover: 'hover:bg-user-secondary-5',
  secondaryBackgroundGroupHover: 'group-hover:bg-user-secondary-5',
  secondaryBackgroundIntense: 'bg-user-secondary-6',
  secondaryBackgroundIntenseHover: 'hover:bg-user-secondary-5',
  secondaryBackgroundMuted: 'bg-user-secondary-3',
  secondaryBackgroundMutedHover: 'hover:bg-user-secondary-4',
  secondaryBackgroundUltraIntense: 'bg-user-secondary-7',
  secondaryBackgroundUltraIntenseHover: 'hover:bg-user-secondary-6',
  secondaryBackgroundUltraMuted: 'bg-user-secondary-1',
  secondaryBackgroundUltraMutedHover: 'hover:bg-user-secondary-2',
  secondaryBorder: 'border-user-secondary-4',
  secondaryBorderHover: 'hover:border-user-secondary-5',
  secondaryBorderGroupHover: 'group-hover:border-user-secondary-5',
  secondaryBorderIntense: 'border-user-secondary-6',
  secondaryBorderUltraIntense: 'border-user-secondary-7',
  secondaryBorderIntenseHover: 'hover:border-user-secondary-5',
  secondaryBorderMuted: 'border-user-secondary-3',
  secondaryBorderUltraMuted: 'border-user-secondary-1',
  secondaryBorderMutedHover: 'hover:border-user-secondary-4',
} as const;

export const ThemeTertiary = {
  tertiaryText: 'text-user-tertiary-4',
  tertiaryTextHover: 'hover:text-user-tertiary-5',
  tertiaryTextIntense: 'text-user-tertiary-6',
  tertiaryTextUltraIntense: 'text-user-tertiary-7',
  tertiaryTextIntenseHover: 'hover:text-user-tertiary-5',
  tertiaryTextMuted: 'text-user-tertiary-3',
  tertiaryTextUltraMuted: 'text-user-tertiary-2',
  tertiaryTextMutedHover: 'hover:text-user-tertiary-4',
  tertiaryShadow: 'shadow shadow-user-tertiary-4/50',
  tertiaryBackground: 'bg-user-tertiary-4',
  tertiaryBackgroundHover: 'hover:bg-user-tertiary-5',
  tertiaryBackgroundGroupHover: 'group-hover:bg-user-tertiary-5',
  tertiaryBackgroundIntense: 'bg-user-tertiary-6',
  tertiaryBackgroundIntenseHover: 'hover:bg-user-tertiary-5',
  tertiaryBackgroundMuted: 'bg-user-tertiary-3',
  tertiaryBackgroundMutedHover: 'hover:bg-user-tertiary-4',
  tertiaryBackgroundUltraIntense: 'bg-user-tertiary-7',
  tertiaryBackgroundUltraIntenseHover: 'hover:bg-user-tertiary-6',
  tertiaryBackgroundUltraMuted: 'bg-user-tertiary-1',
  tertiaryBackgroundUltraMutedHover: 'hover:bg-user-tertiary-2',
  tertiaryBorder: 'border-user-tertiary-4',
  tertiaryBorderHover: 'hover:border-user-tertiary-5',
  tertiaryBorderGroupHover: 'group-hover:border-user-tertiary-5',
  tertiaryBorderIntense: 'border-user-tertiary-6',
  tertiaryBorderUltraIntense: 'border-user-tertiary-7',
  tertiaryBorderIntenseHover: 'hover:border-user-tertiary-5',
  tertiaryBorderMuted: 'border-user-tertiary-3',
  tertiaryBorderUltraMuted: 'border-user-tertiary-1',
  tertiaryBorderMutedHover: 'hover:border-user-tertiary-4',
} as const;

export const ThemePopup = {
  background: 'bg-user-gray-3',
  backgroundInverted: 'bg-user-gray-5',
  backgroundHover: 'hover:bg-user-gray-4',
  mainText: 'text-user-gray-7',
  secondaryText: 'text-user-gray-6',
  tertiaryText: 'text-user-gray-5',
  border: 'border-user-gray-4',
  divide: 'divide-user-gray-4',
  shadow: 'shadow-md shadow-user-gray-7/20',
} as const;

// Combined THEME constant for quick access
export const Theme = {
  background: ThemeBackground,
  text: ThemeText,
  primary: ThemePrimary,
  secondary: ThemeSecondary,
  tertiary: ThemeTertiary,
  popup: ThemePopup,
} as const;
